import type { UNABLE_TO_CALCULATE } from './remediation';

export enum Exploitability {
  // attempts have been made to exploit
  Mature = 'MATURE',
  // a proof of concept exists
  POC = 'POC',
  // no data on exploitability
  Unknown = 'UNKNOWN',
}

// Types from the CWE Data dump (modules/fixtures/cwe-data.ts)
export type CWEConsequence = {
  'Scope': string | string[];
  'Impact'?: string | string[];
  'Note'?: string;
  'Likelihood'?: string;
};

export type CWEMitigation = {
  // Sometimes this is a string, and sometimes it's a randomly shaped object, so we define as unknown
  'Description': unknown;
  'Phase'?: string | string[];
  'Mitigation_ID'?: string;
  'Strategy'?: string;
  'Effectiveness'?: string;
  'Effectiveness_Notes'?: unknown;
}

export type Weakness = {
  id: number;
  title: string;
  status: string;
  description: string;
  // Sometimes this is a string, and sometimes it's a randomly shaped object, so we define as unknown
  extended_description?: unknown;
  exploitability?: string;
  consequences?: CWEConsequence | CWEConsequence[];
  mitigations?: CWEMitigation | CWEMitigation[];
}

// This is the shape of the auto-generated cwe-date file
export type CWEFile = {
  'Name': string;
  'Version': string;
  'Date': string;
  'xmlns:xsi': string;
  'xsi:schemaLocation': string;
  'xmlns:xhtml': string;
  dateGenerated: number;
  latestCWE: number;
  data: Record<string, Weakness>;
}

export type MinimalWeakness = Pick<Weakness, 'id' | 'title' | 'description'>;

export enum UpgradeDistance {
  MAJOR = 'MAJOR',
  MINOR = 'MINOR',
  PATCH = 'PATCH',
}

export type NextSafeVersions = {
  completeFix: string;
  completeFixDistance?: UpgradeDistance,
  partialFix: string;
  partialFixDistance?: UpgradeDistance;
};

export type CveWithDetails = {
  cve: string;
  description?: string;
};

// /api/vulns/cve-list
export type CveListRequest = {
  query: string;
}

export type CveListResponse = CveWithDetails[];

// /api/vulns/:vulnId
export type VulnRequest = {
  vulnId: string;
}

// /api/weaknesses
export type WeaknessesResponse = MinimalWeakness[]

// /api/v2/vulns/next-safe-version
export type NextSafeVersionRequest = {
  revisionId: string;
  vulnId: string;
}

// /api/vulns/:vulnId/revisions/:revisionId/remediation-guidance
/**
 * Next safe version response for vulnerabilities containing a complete (all vulns are remediated with this version)
 * and partial (only this vuln is remediated with this version) fix.
 */
export type NextSafeVersionResponse = NextSafeVersions;

// /api/vulns/revision/:revisionId/vuln/:vulnId/next-safe-version
export type PartialNextSafeVersionRequest = NextSafeVersionRequest;

export type PartialNextSafeVersionResponse = {
  nextSafeVersion: string;
}

// /api/vulns/revision/:revisionId/next-safe-version
export type CompleteNextSafeVersionRequest = {
  revisionId: string;
}

export type CompleteNextSafeVersionResponse = {
  nextSafeVersion: string;
};

// /api/vulns/revisions/next-safe-version
export type VulnsNextSafeVersionsForPackages = Record<string, string>;

export type VulnsOverallSafeVersion = {
  overallRemediation: string;
}

export type VulnsNextSafeVersionList = Record<string, VulnsNextSafeVersionsForPackages & VulnsOverallSafeVersion>;

export type NextSafeVersionListRequest = {
  revisions: string[];
}

export type VulnSeverityBounds = {
  high: { op: string, value: number },
  low: { op: string, value: number },
}

export type NextSafeVersionListResponse = VulnsNextSafeVersionList | typeof UNABLE_TO_CALCULATE;

export type CVSSMetric = {
  name: string;
  value: string;
}

export type FixDetails = {
  /**
   * Next safe version that'll either be:
   *   1. a version string
   *   2. `NO_SAFE_VERSION`
   *   3. `UNABLE_TO_CALCULATE`
   */
  nextSafeVersion: string;

  /**
   * Optional next safe version upgrade distance (PATCH, MINOR, or MAJOR)
   */
  nextSafeVersionDistance?: UpgradeDistance;
};
